import React, { useCallback, useEffect, useState } from 'react';
import { BellRing, Laptop, Lock, RefreshCw, Shield, ShieldAlert } from 'lucide-react';
import { safeFetch } from '../../utils/safeFetch';
import { API_URL } from '../../config';
import { CompactListSkeleton } from '../../components/Skeleton';
import { haptic } from '../../utils/vibration';
import useToast from '../../hooks/useToast';
import { formatTimezoneDate } from '../../utils/timezone';
import '../../styles/Management.css';

const ATTEMPTS_PAGE_SIZE = 50;

const SecurityToggle = ({ checked, disabled, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label="Push разработчику при неудачной попытке входа"
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className="toggle-switch shrink-0 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus-visible:ring-offset-gray-900"
    style={{
      '--toggle-bg': checked ? 'rgb(220 38 38)' : 'rgb(156 163 175)',
      '--toggle-height': '28px',
      '--toggle-width': '48px',
      '--knob-x': checked ? '22px' : '2px',
    }}
  >
    <span className="toggle-switch-knob" />
  </button>
);

const failureReasonLabel = (reason) => (
  reason === 'unknown_login' ? 'Логин не найден' : 'Неверный пароль'
);

const SecurityManagement = () => {
  const [blockedIPs, setBlockedIPs] = useState([]);
  const [loginAttempts, setLoginAttempts] = useState([]);
  const [attemptsTotal, setAttemptsTotal] = useState(0);
  const [developerPushEnabled, setDeveloperPushEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [savingPushSetting, setSavingPushSetting] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  const fetchSecurityData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [blockedData, attemptsData, pushData] = await Promise.all([
        safeFetch(`${API_URL}/api/blocked-ips`),
        safeFetch(`${API_URL}/api/security/login-attempts?limit=${ATTEMPTS_PAGE_SIZE}&offset=0`),
        safeFetch(`${API_URL}/api/security/failed-login-push`),
      ]);
      setBlockedIPs(blockedData.blocked_ips || []);
      setLoginAttempts(attemptsData.attempts || []);
      setAttemptsTotal(Number(attemptsData.total || 0));
      setDeveloperPushEnabled(Boolean(pushData.enabled));
    } catch (fetchError) {
      console.error('Ошибка загрузки раздела безопасности:', fetchError);
      const msg = fetchError.message || 'Не удалось загрузить данные безопасности';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMoreAttempts = async () => {
    setLoadingMore(true);
    setError('');
    try {
      const data = await safeFetch(
        `${API_URL}/api/security/login-attempts?limit=${ATTEMPTS_PAGE_SIZE}&offset=${loginAttempts.length}`
      );
      setLoginAttempts((current) => [...current, ...(data.attempts || [])]);
      setAttemptsTotal(Number(data.total || 0));
    } catch (fetchError) {
      console.error('Ошибка загрузки журнала входов:', fetchError);
      const msg = fetchError.message || 'Не удалось загрузить журнал входов';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setLoadingMore(false);
    }
  };

  const updateDeveloperPush = async (enabled) => {
    haptic('selection');
    setSavingPushSetting(true);
    setError('');
    try {
      const data = await safeFetch(`${API_URL}/api/security/failed-login-push`, {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
      });
      setDeveloperPushEnabled(Boolean(data.enabled));
      showToast('Настройки push-уведомлений сохранены', 'success');
    } catch (saveError) {
      console.error('Ошибка сохранения push-настройки:', saveError);
      const msg = saveError.message || 'Не удалось сохранить настройку push';
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setSavingPushSetting(false);
    }
  };

  const unblockIP = async (ip) => {
    haptic('warning');
    try {
      await safeFetch(`${API_URL}/api/unblock-ip`, {
        method: 'POST',
        body: JSON.stringify({ ip }),
      });
      showToast('IP разблокирован', 'success');
      await fetchSecurityData();
    } catch (unblockError) {
      console.error('Ошибка разблокировки IP:', unblockError);
      const msg = unblockError.message || 'Не удалось разблокировать IP';
      setError(msg);
      showToast(msg, 'error');
    }
  };

  useEffect(() => {
    fetchSecurityData();
  }, [fetchSecurityData]);

  return (
    <div className="space-y-4 animate-fade-in desktop-content-container-wide">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-950 dark:text-white">Безопасность входа</h2>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            Ошибки авторизации, блокировки и оповещения разработчика
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            haptic('tap');
            fetchSecurityData();
          }}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Обновить</span>
        </button>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      ) : null}

      <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300">
              <BellRing className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-950 dark:text-white">
                Push при неудачном входе
              </h3>
              <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                Отправлять разработчикам логин и IP. Введённый пароль никогда не сохраняется и не передаётся.
              </p>
            </div>
          </div>
          <SecurityToggle
            checked={developerPushEnabled}
            disabled={loading || savingPushSetting}
            onChange={updateDeveloperPush}
          />
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-red-600" />
            <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
              Неудачные попытки ({attemptsTotal})
            </h3>
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400">Сначала новые</span>
        </div>

        {loading && loginAttempts.length === 0 ? (
          <CompactListSkeleton count={4} />
        ) : loginAttempts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white py-10 text-center dark:border-gray-700 dark:bg-gray-900">
            <Shield className="mx-auto mb-3 h-10 w-10 text-emerald-500" />
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Ошибок входа пока нет</h4>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Журнал будет заполняться автоматически</p>
          </div>
        ) : (
          <div className="space-y-2">
            {loginAttempts.map((attempt) => (
              <article
                key={attempt.id}
                className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-gray-950 dark:text-white">{attempt.attempted_login}</span>
                      <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700 dark:bg-red-950/40 dark:text-red-300">
                        {failureReasonLabel(attempt.failure_reason)}
                      </span>
                      {attempt.triggered_ip_block ? (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                          IP заблокирован
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                      <span className="font-mono text-gray-700 dark:text-gray-300">{attempt.ip_address}</span>
                      <span>{formatTimezoneDate(attempt.created_at, true)}</span>
                    </div>
                    {attempt.user_agent ? (
                      <div className="mt-2 flex min-w-0 items-center gap-1.5 text-[11px] text-gray-400 dark:text-gray-500">
                        <Laptop className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate" title={attempt.user_agent}>{attempt.user_agent}</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              </article>
            ))}

            {loginAttempts.length < attemptsTotal ? (
              <button
                type="button"
                onClick={loadMoreAttempts}
                disabled={loadingMore}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                {loadingMore ? 'Загрузка…' : 'Показать ещё'}
              </button>
            ) : null}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Lock className="h-5 w-5 text-red-600" />
          <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
            Заблокированные IP ({blockedIPs.length})
          </h3>
        </div>

        {loading && blockedIPs.length === 0 ? (
          <CompactListSkeleton count={2} />
        ) : blockedIPs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white py-8 text-center dark:border-gray-700 dark:bg-gray-900">
            <Lock className="mx-auto mb-3 h-9 w-9 text-gray-400" />
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Нет заблокированных IP-адресов</h4>
          </div>
        ) : (
          <div className="grid gap-3">
            {blockedIPs.map((ipData) => (
              <div
                key={ipData.ip}
                className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-sm font-semibold text-gray-900 dark:text-gray-100">{ipData.ip}</div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Последняя попытка: {formatTimezoneDate(ipData.last_attempt, true)}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800 dark:bg-red-900/20 dark:text-red-400">
                        {ipData.failed_attempts} попыток
                      </span>
                      <span className="rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-800 dark:bg-orange-900/20 dark:text-orange-400">
                        Осталось {ipData.remaining_time_minutes} мин
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => unblockIP(ipData.ip)}
                    aria-label={`Разблокировать IP ${ipData.ip}`}
                    className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-red-600 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-red-700"
                  >
                    <Shield className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Разблокировать</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default SecurityManagement;
